import { z } from 'zod';
import {
  type AccessLevel,
  accessLevelSchema,
  type DataScope,
  DEFAULT_MEMBERSHIP_FLAGS,
  dataScopeSchema,
  GRANT_LEVELS,
  type GrantLevel,
  type GrantResourceType,
  grantResourceTypeSchema,
  type MembershipFlags,
  type MembershipStatus,
  MODULES,
  type ModuleKey,
  membershipFlagsSchema,
  PERMISSION_TEMPLATE_DEFAULTS,
  type PermissionTemplate,
  permissionTemplateSchema,
  type Role,
  roleSchema,
} from '../enums';
import { emailSchema, idSchema, versionSchema } from './common';

/**
 * Zespół, role i uprawnienia (docs/PERMISSIONS_AND_WORKSPACES.md §2–§3). tRPC `team.*`,
 * REST `POST /api/auth/invitation/preview` i `POST /api/auth/invitation/accept` (docs/API_AUTH.md).
 */

// ── Stałe ───────────────────────────────────────────────────────────────────
/** Zaproszenie jest ważne 7 dni (link z tokenem 32 B, w bazie SHA-256). */
export const INVITATION_TTL_DAYS = 7;
export const INVITATION_TTL_SEC = INVITATION_TTL_DAYS * 24 * 3600;
/** Zmiany w zespole wymagają potwierdzenia hasłem nie starszego niż 10 min. */
export const TEAM_STEP_UP_MAX_AGE_SEC = 600;

/** Role nadawane zaproszeniem i zmianą roli. Właściciel tylko przez przekazanie własności (poza MVP). */
export const ASSIGNABLE_ROLES = ['user', 'manager', 'admin'] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];
export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES, { errorMap: () => ({ message: 'Wybierz rolę' }) });

/** Ścieżki SPA: link z maila zaproszenia i ekrany Ustawień → Zespół. */
export const TEAM_WEB_PATHS = {
  /** `?token=...` */
  invitation: '/zaproszenie',
  team: '/ustawienia/zespol',
} as const;

export function memberPermissionsPath(membershipId: string): string {
  return `${TEAM_WEB_PATHS.team}/${membershipId}`;
}

export const TEAM_MESSAGES = {
  manageOnly: 'Tylko administrator zarządza zespołem',
  viewForbidden: 'Ustawienia zespołu widzą administratorzy i managerowie.',
  ownerViaTransfer: 'Właściciela zmienia się przez przekazanie własności (wkrótce).',
  lastOwner: 'Nie można odebrać roli ostatniemu właścicielowi workspace’u.',
  ownerProtected: 'Ustawienia właściciela zmienia tylko właściciel.',
  ownerCannotBeDisabled: 'Nie można wyłączyć dostępu właściciela workspace’u.',
  cannotDisableSelf: 'Nie możesz wyłączyć własnego dostępu.',
  alreadyDisabled: 'Dostęp tej osoby jest już wyłączony.',
  alreadyActive: 'Ta osoba ma już aktywny dostęp.',
  templateUserOnly: 'Szablon uprawnień dotyczy roli Użytkownik.',
  approvesAiAllUser: 'Użytkownik zatwierdza najwyżej swoje akcje AI. „Wszystkie” mają tylko Manager i Admin.',
  alreadyMember: 'Ta osoba już jest w zespole.',
  portalPerson: 'Ta osoba ma dostęp do portalu klienta. Najpierw cofnij jej dostęp do portalu.',
  portalRoleViaInvite: 'Dostęp do portalu klienta nadaje się zaproszeniem na karcie klienta.',
  memberDisabled: 'Ta osoba ma wyłączony dostęp. Włącz go ponownie na liście zespołu.',
  invitationPending: 'Zaproszenie dla tego adresu już czeka. Możesz wysłać je ponownie.',
  invitationNotFound: 'Nie znaleziono zaproszenia.',
  invitationInvalid: 'Zaproszenie jest nieprawidłowe albo zostało anulowane.',
  invitationExpired: 'Zaproszenie wygasło. Poproś administratora o nowe.',
  invitationUsed: 'To zaproszenie zostało już wykorzystane. Zaloguj się, aby wejść do workspace’u.',
  invitationLoginRequired: 'Masz już konto z tym adresem. Zaloguj się, aby dołączyć.',
  invitationOtherAccount: 'To zaproszenie jest dla innego adresu email. Wyloguj się i otwórz link ponownie.',
  invitationMembershipDisabled: 'Twój dostęp do tego workspace’u jest wyłączony. Skontaktuj się z administratorem.',
  resourceNotFound: 'Nie znaleziono wskazanego klienta, projektu albo zasobu.',
  duplicateGrant: 'Ten dostęp jest już na liście.',
  nameRequired: 'Podaj imię i nazwisko',
  passwordRequired: 'Podaj hasło',
} as const;

/** Powód błędu zaproszenia (`error.details.reason` w REST). */
export const INVITATION_ERROR_REASONS = [
  'invalid',
  'expired',
  'used',
  'login_required',
  'other_account',
  'disabled',
] as const;
export type InvitationErrorReason = (typeof INVITATION_ERROR_REASONS)[number];

// ── Klocki uprawnień ────────────────────────────────────────────────────────
export type ModuleLevels = Record<ModuleKey, AccessLevel>;

/** Komplet poziomów modułów (każdy moduł: none / view / edit). */
export const moduleLevelsSchema = z.object(
  Object.fromEntries(MODULES.map((m) => [m, accessLevelSchema])) as Record<ModuleKey, typeof accessLevelSchema>,
);
/** Część modułów (pozostałe bez zmian albo „Brak”). */
export const partialModuleLevelsSchema = moduleLevelsSchema.partial();

export function emptyModuleLevels(): ModuleLevels {
  return Object.fromEntries(MODULES.map((m) => [m, 'none'])) as ModuleLevels;
}

/** Uzupełnia brakujące moduły poziomem „Brak”. */
export function normalizeModuleLevels(
  partial: Partial<Record<ModuleKey, AccessLevel>> | null | undefined,
): ModuleLevels {
  const out = emptyModuleLevels();
  for (const m of MODULES) {
    const level = partial?.[m];
    if (level === 'view' || level === 'edit') out[m] = level;
  }
  return out;
}

/** Flagi członkostwa (komplet). */
export const memberFlagsSchema = membershipFlagsSchema.strict();
export const partialMemberFlagsSchema = membershipFlagsSchema.partial().strict();

export function normalizeMemberFlags(partial: Partial<MembershipFlags> | null | undefined): MembershipFlags {
  return { ...DEFAULT_MEMBERSHIP_FLAGS, ...(partial ?? {}) };
}

/** Limit AI w zł na miesiąc (numeric(10,2)); `null` = bez limitu osobistego. */
export const aiMonthlyLimitSchema = z
  .string()
  .trim()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, { message: 'Podaj kwotę w zł, np. 30 albo 30.50' });

/** Dostęp do pojedynczego klienta, projektu, zasobu albo sejfu zasobu (`access_grants`). */
export const grantInputSchema = z.object({
  resourceType: grantResourceTypeSchema,
  resourceId: idSchema,
  level: z.enum(GRANT_LEVELS).default('view'),
});
export type GrantInput = z.input<typeof grantInputSchema>;
export type GrantValue = z.output<typeof grantInputSchema>;

function noDuplicateGrants(grants: GrantValue[], ctx: z.RefinementCtx, path: (string | number)[]): void {
  const seen = new Set<string>();
  grants.forEach((g, i) => {
    const key = `${g.resourceType}:${g.resourceId}`;
    if (seen.has(key))
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: TEAM_MESSAGES.duplicateGrant, path: [...path, i] });
    seen.add(key);
  });
}

/** Pełny zestaw uprawnień osoby (warstwy z PERMISSIONS §3: moduły, zakres, granty, flagi, limit AI). */
export const permissionSetShape = {
  template: permissionTemplateSchema.nullable(),
  modules: moduleLevelsSchema,
  dataScope: dataScopeSchema,
  flags: memberFlagsSchema,
  aiMonthlyLimitPln: aiMonthlyLimitSchema.nullable(),
  /** komplet grantów (klienci przy zakresie „Wybrani klienci”, projekty, zasoby, sejf zasobu); zastępuje obecne */
  grants: z.array(grantInputSchema).max(500),
};
export const permissionSetSchema = z
  .object(permissionSetShape)
  .superRefine((v, ctx) => noDuplicateGrants(v.grants, ctx, ['grants']));
export type PermissionSet = z.output<typeof permissionSetSchema>;

export interface TemplatePermissions {
  modules: ModuleLevels;
  dataScope: DataScope;
  flags: MembershipFlags;
  /** domyślny limit AI szablonu (Partner: `'0'` = AI wyłączone); null = szablon nie ustawia limitu */
  aiMonthlyLimitPln: string | null;
}

/** Punkt startowy szablonu (PERMISSIONS §3.4): moduły spoza szablonu = „Brak”, flagi spoza szablonu = wyłączone. */
export function templatePermissions(template: PermissionTemplate): TemplatePermissions {
  const def = PERMISSION_TEMPLATE_DEFAULTS[template];
  return {
    modules: normalizeModuleLevels(def.modules),
    dataScope: def.dataScope,
    flags: normalizeMemberFlags(def.flags),
    aiMonthlyLimitPln: def.aiMonthlyLimitPln ?? null,
  };
}

/**
 * Limit AI osoby po zmianie szablonu (zaproszenie, uprawnienia osoby w Zespole, `team.applyTemplate`): szablon
 * z domyślnym limitem (Partner: 0 zł) go ustawia; przejście z takiego szablonu na inny (albo „Własne ustawienia”)
 * zdejmuje limit, który został z szablonu — kwota wpisana ręcznie zostaje. null = bez limitu osobistego.
 */
export function aiLimitAfterTemplate(
  prev: PermissionTemplate | null,
  next: PermissionTemplate | null,
  current: string | null,
): string | null {
  const nextDefault = next ? PERMISSION_TEMPLATE_DEFAULTS[next].aiMonthlyLimitPln : undefined;
  if (nextDefault !== undefined) return nextDefault;
  const prevDefault = prev ? PERMISSION_TEMPLATE_DEFAULTS[prev].aiMonthlyLimitPln : undefined;
  if (prevDefault !== undefined && current !== null && Number(current) === Number(prevDefault)) return null;
  return current;
}

// ── Wejścia usług (tRPC team.*) ─────────────────────────────────────────────
export const memberRefInput = z.object({ membershipId: idSchema });
export type MemberRefInput = z.infer<typeof memberRefInput>;

export const teamOverviewInput = z.object({}).strict().optional();
export const grantOptionsInput = z.object({}).strict().optional();
export const getMemberPermissionsInput = memberRefInput;

export const inviteMemberInput = z
  .object({
    email: emailSchema,
    role: assignableRoleSchema,
    /** tylko rola Użytkownik; brak = bez szablonu (moduły z `modules`) */
    template: permissionTemplateSchema.nullable().default(null),
    /** domyślnie z szablonu, bez szablonu „Wybrani klienci” */
    dataScope: dataScopeSchema.optional(),
    /** klienci udostępnieni od razu (grant „client”, poziom Edycja; moduły i tak ograniczają) */
    clientIds: z.array(idSchema).max(200).default([]),
    /** dodatkowe granty: projekty, zasoby, sejf zasobu */
    grants: z.array(grantInputSchema).max(200).default([]),
    /** nadpisania modułów względem szablonu */
    modules: partialModuleLevelsSchema.optional(),
    /** nadpisania flag względem szablonu */
    flags: partialMemberFlagsSchema.optional(),
    /** brak = domyślny limit szablonu (Partner: 0 zł = AI wyłączone), bez szablonu — bez limitu osobistego */
    aiMonthlyLimitPln: aiMonthlyLimitSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => noDuplicateGrants(v.grants, ctx, ['grants']));
export type InviteMemberInput = z.input<typeof inviteMemberInput>;

export const invitationRefInput = z.object({ invitationId: idSchema });
export type InvitationRefInput = z.infer<typeof invitationRefInput>;

/** Rola `client` (portal klienta) jest odrzucana w usłudze: osoba klienta ma dostęp tylko z zaproszenia do portalu. */
export const updateRoleInput = z.object({ membershipId: idSchema, role: roleSchema, version: versionSchema });
export type UpdateRoleInput = z.infer<typeof updateRoleInput>;

export const updatePermissionsInput = z
  .object({ membershipId: idSchema, version: versionSchema, ...permissionSetShape })
  .superRefine((v, ctx) => noDuplicateGrants(v.grants, ctx, ['grants']));
export type UpdatePermissionsInput = z.input<typeof updatePermissionsInput>;

export const applyTemplateInput = z.object({
  membershipId: idSchema,
  template: permissionTemplateSchema,
  version: versionSchema,
});
export type ApplyTemplateInput = z.infer<typeof applyTemplateInput>;

/** Dezaktywacja / ponowna aktywacja (wersja opcjonalna: CONFLICT, gdy ktoś zmienił osobę w międzyczasie). */
export const setMemberStatusInput = z.object({ membershipId: idSchema, version: versionSchema.optional() });
export type SetMemberStatusInput = z.infer<typeof setMemberStatusInput>;

export const removeGrantInput = z.object({ grantId: idSchema });
export type RemoveGrantInput = z.infer<typeof removeGrantInput>;

// ── Wyjścia (DTO) ───────────────────────────────────────────────────────────
export interface TeamMemberItem {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
  role: Role;
  status: MembershipStatus;
  template: PermissionTemplate | null;
  dataScope: DataScope;
  /** liczba udostępnionych klientów (zakres „Wybrani” / „Tylko przypisani”); null = wszyscy klienci */
  clientCount: number | null;
  aiMonthlyLimitPln: string | null;
  lastLoginAt: Date | null;
  joinedAt: Date;
  isSelf: boolean;
  version: number;
}

export const INVITATION_STATUSES = ['pending', 'expired'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export interface TeamInvitationItem {
  id: string;
  email: string;
  role: Role;
  template: PermissionTemplate | null;
  dataScope: DataScope;
  status: InvitationStatus;
  clientCount: number | null;
  aiMonthlyLimitPln: string | null;
  invitedBy: { userId: string; name: string } | null;
  createdAt: Date;
  expiresAt: Date;
}

export interface TeamOverview {
  members: TeamMemberItem[];
  /** zaproszenia jeszcze nieprzyjęte (czekające i wygasłe) */
  invitations: TeamInvitationItem[];
  /** czy osoba może zapraszać, zmieniać role i uprawnienia (Właściciel, Admin) */
  canManage: boolean;
}

export interface MemberGrant {
  id: string;
  resourceType: GrantResourceType;
  resourceId: string;
  level: GrantLevel;
  /** nazwa klienta / projektu / zasobu; „Usunięty rekord”, gdy już nie istnieje */
  label: string;
  /** np. klient projektu albo adres zasobu */
  description: string | null;
  /** kolor klienta (dane) */
  color: string | null;
  missing: boolean;
}

export interface MemberPermissions {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
  role: Role;
  status: MembershipStatus;
  version: number;
  isSelf: boolean;
  lastLoginAt: Date | null;
  joinedAt: Date;
  template: PermissionTemplate | null;
  dataScope: DataScope;
  modules: ModuleLevels;
  flags: MembershipFlags;
  aiMonthlyLimitPln: string | null;
  grants: MemberGrant[];
}

export interface GrantOptions {
  clients: { id: string; name: string; color: string | null }[];
  projects: { id: string; name: string; clientId: string | null; clientName: string | null; color: string | null }[];
  assets: {
    id: string;
    name: string;
    address: string | null;
    clientId: string;
    clientName: string | null;
    color: string | null;
  }[];
}

export interface MemberStatusResult {
  member: MemberPermissions;
  /** unieważnione sesje tej osoby w tym workspace (dezaktywacja) */
  revokedSessions: number;
  /** unieważnione tokeny API (MCP, runner) tej osoby w tym workspace */
  revokedTokens: number;
}

// ── REST: zaproszenia (/api/auth/invitation/*) ──────────────────────────────
export const invitationTokenSchema = z.string().trim().min(16).max(256);

export const invitationPreviewInput = z.object({ token: invitationTokenSchema });
export type InvitationPreviewInput = z.infer<typeof invitationPreviewInput>;

export const invitationPreviewSchema = z.object({
  email: z.string(),
  workspace: z.object({ name: z.string() }),
  inviterName: z.string().nullable(),
  role: roleSchema,
  expiresAt: z.string(),
  /** konto z tym adresem już istnieje: dołączenie po zalogowaniu (bez nowego hasła) */
  accountExists: z.boolean(),
  /** zaproszenie do portalu klienta (rola `client`): nazwa klienta; null = zaproszenie do zespołu */
  portalClientName: z.string().nullable().default(null),
  /** imię z zaproszenia do portalu (podpowiedź formularza nowego konta) */
  suggestedName: z.string().nullable().default(null),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

/**
 * Body `POST /api/auth/invitation/accept`. Nowe konto: `name` + `password` (polityka haseł).
 * Konto już istnieje: tylko `token`, osoba musi być zalogowana na to konto.
 */
export const acceptInvitationBody = z.object({
  token: invitationTokenSchema,
  name: z
    .string()
    .trim()
    .min(2, { message: TEAM_MESSAGES.nameRequired })
    .max(120, { message: 'Najwyżej 120 znaków' })
    .optional(),
  password: z.string().min(1, { message: TEAM_MESSAGES.passwordRequired }).max(1024).optional(),
});
export type AcceptInvitationBody = z.infer<typeof acceptInvitationBody>;

export const acceptInvitationOutput = z.object({
  ok: z.literal(true),
  workspace: z.object({ id: idSchema, name: z.string(), slug: z.string() }),
  /** true = założono konto i zalogowano */
  newAccount: z.boolean(),
});
export type AcceptInvitationOutput = z.infer<typeof acceptInvitationOutput>;
